import React, { createContext, useContext, useState } from "react";
import {
    PublicClientApplication,
} from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import apis from "../utils/apis";
import { translate } from '../i18n/translate';
import type { ExternalPickedItem } from '../components/shared/files/filesPickerTypes';
import { INTEGRATIONS_SETTINGS_PATH } from '../components/shared/files/filesPickerTypes';
import { IntegrationNotConnectedError } from '../components/shared/files/externalImport';

/**
 * Where the picker's sign-in popup returns, from wherever it was opened.
 * One fixed page, so each environment registers a single redirect URI.
 */
export const PICKER_REDIRECT_PATH = '/onedrive-picker.html';

export interface OneDrivePickOptions {
    /** Extensions without dots; narrows the picker's filter. */
    accept?: string[];
    /** Default true. */
    multiple?: boolean;
}

const CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || "";
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || window.location.origin;

const msalConfig = {
    auth: {
        clientId: CLIENT_ID,
        redirectUri: REDIRECT_URI,
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);

interface MicrosoftGraphContextProps {
    accessToken: string | null;
    isSignedIn: boolean;
    MSALsignIn: () => Promise<any>;
    signOut: () => void;
    uploadFileToOneDrive: (fileName: string, fileContent: Blob, SharedWith: any[]) => Promise<any>;
    getEditLink: (fileId: string) => Promise<string>;
    downloadFileFromOneDrive: (fileId: string) => Promise<Blob>;
    deleteFileFromOneDrive: (fileId: string) => Promise<void>;
    restrictSharing: (fileId: string) => Promise<void>;
    fetchCurrentUserDetails: () => Promise<any>;
    /** Opens the OneDrive / SharePoint picker; resolves with item ids (null on cancel). */
    pickItems: (options?: OneDrivePickOptions) => Promise<ExternalPickedItem[] | null>;
    /** @deprecated Use `pickItems`. */
    handleOpenMicrosoftPicker: (onPicked: (items: ExternalPickedItem[]) => void) => Promise<void>;
    unlockFileOnOneDrive: (fileId: string) => Promise<void>;
}

const defaultContext: MicrosoftGraphContextProps = {
    accessToken: null,
    isSignedIn: false,
    MSALsignIn: async () => { },
    signOut: () => { },
    uploadFileToOneDrive: async () => { },
    getEditLink: async () => "",
    downloadFileFromOneDrive: async () => new Blob(),
    deleteFileFromOneDrive: async () => { },
    restrictSharing: async () => { },
    fetchCurrentUserDetails: async () => { },
    pickItems: async () => null,
    handleOpenMicrosoftPicker: async () => { },
    unlockFileOnOneDrive: async () => { },
};
let localAccessToken: any = null
/**
 * The client id of the app that holds the connection ("PONS Integrations"),
 * sent with its token. Deployed builds have no REACT_APP_MICROSOFT_CLIENT_ID
 * (the pipeline never sets it), so this is the only id the picker can open with.
 */
let tokenClientId: string | null = null
const MicrosoftGraphContext = createContext<MicrosoftGraphContextProps>(defaultContext);

export const MicrosoftGraphProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [isSignedIn, setIsSignedIn] = useState(false);
    /** A fresh backend-held OneDrive token, or null when not connected. Never redirects. */
    const fetchBackendToken = async (): Promise<string | null> => {
        try {
            const response = await apis.get<{ access_token: string; client_id?: string | null }>("/integrations/auth/tokens/onedrive");
            const token = response?.status === 200 ? response.data?.access_token : null;
            tokenClientId = response?.data?.client_id || null;
            if (token) {
                setAccessToken(token);
                setIsSignedIn(true);
                localAccessToken = token;
            }
            return token || null;
        } catch {
            return null;
        }
    };

    const MSALsignIn = async () => {
        // Redirect to integrations settings to connect OneDrive
        window.location.href = INTEGRATIONS_SETTINGS_PATH;
    };

    const signOut = () => {
    };


    const getGraphClient = async () => {
        if (!accessToken) {
            return Client.init({
                authProvider: (done: any) => {
                    done(null, localAccessToken);
                },
            });
        } else {
            return Client.init({
                authProvider: (done: any) => {
                    done(null, accessToken);
                },
            });

        }
    };

    const uploadFileToOneDrive = async (fileName: string, fileContent: Blob, SharedWith: any[]): Promise<any> => {
        try {
            const client = await getGraphClient();
            let updatedSharedWith = null

            try {
                const response = await client.api(`/me/drive/root:/${fileName}`).get();
                if (response && response.id) {
                    return { fileID: response.id, updatedSharedWith: updatedSharedWith, webURL: response.webUrl };
                }
            } catch (error: any) {
                if (error.statusCode === 404) {
                    const response = await client
                        .api(`/me/drive/root:/${fileName}:/content?@microsoft.graph.conflictBehavior=replace`)
                        .put(fileContent);

                    if (!response || !response.id) {
                        throw new Error("Failed to upload file: No response or missing file ID.");
                    }
                    const mappedEmails = SharedWith.map(({ Email, Permission }) => ({
                        email: Email,
                        roles: [Permission]
                    })) || [];
                    if (mappedEmails.length > 0) {
                        const { sharedLinks } = await shareAccess(response.id, mappedEmails)

                        updatedSharedWith = SharedWith.map(user => {
                            const sharedLinkEntry = sharedLinks.find(link => link.email === user.Email);
                            return {
                                ...user,
                                SharedURL: sharedLinkEntry ? sharedLinkEntry.link : null, // Add link if found
                            };
                        });

                    }

                    return { fileID: response.id, updatedSharedWith: updatedSharedWith, webURL: null };
                } else {
                    throw error;
                }
            }
        } catch (error) {
            throw error;
        }
    };

    const getEditLink = async (fileId: string): Promise<string> => {
        const client = await getGraphClient();
        const response = await client.api(`/me/drive/items/${fileId}/createLink`).post({
            type: 'edit',
            scope: 'anonymous'
        });
        if (!response) {
            const error = await response;
            throw new Error(`Error creating link: ${error.error}`);
        }

        const data = await response;

        return data.link.webUrl;
    };

    const downloadFileFromOneDrive = async (fileId: string): Promise<Blob> => {
        try {
            const fileContent = await fetchFileContent(fileId, accessToken)
            return fileContent;
        } catch (error) {
            throw error;
        }
    };

    const deleteFileFromOneDrive = async (fileId: string) => {
        try {
            const url = `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}`;
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({})
            });

            if (response.status === 204) {
            } else {
                const errorDetails = await response.json();
                throw new Error(errorDetails.error.message);
            }
        } catch (error) {
            throw error;
        }
    };
    const restrictSharing = async (fileId: string): Promise<void> => {
        const client = await getGraphClient();
        const permissions = await client.api(`/me/drive/items/${fileId}/permissions`).get();

        for (const permission of permissions.value) {
            await client.api(`/me/drive/items/${fileId}/permissions/${permission.id}`).delete();
        }

    };

    const fetchCurrentUserDetails = async (): Promise<any> => {
        const client = await getGraphClient();
        const user = await client.api('/me').get();
        return user;
    };

    const fetchFileContent = async (fileId: string, token: any): Promise<Blob> => {
        try {
            const response = await fetch(
                `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken ? accessToken : token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch file content: ${response.statusText}`);
            }

            const blob = await response.blob();
            return blob;
        } catch (error) {
            throw error;
        }
    };




    /**
     * Open the OneDrive / SharePoint picker (v7.2) and resolve with the picked
     * ITEM ids plus their drive (`parentReference.driveId`) - F-DMS.
     *
     * Nothing is downloaded in the browser: the caller hands the ids to
     * `POST /integrations/onedrive/import`. Resolves `null` on cancel; rejects
     * with `IntegrationNotConnectedError` when OneDrive is not connected.
     */
    const pickItems = async (options?: OneDrivePickOptions): Promise<ExternalPickedItem[] | null> => {
        const activeToken = await fetchBackendToken();
        if (!activeToken) throw new IntegrationNotConnectedError('onedrive');
        if (typeof OneDrive === 'undefined' || !OneDrive?.open) {
            throw new Error('OneDrive picker is not available');
        }
        const pickerClientId = tokenClientId || CLIENT_ID;
        if (!pickerClientId) throw new Error('No Microsoft client id to open the picker with');

        return new Promise<ExternalPickedItem[] | null>((resolve, reject) => {
            const extensions = (options?.accept || []).map((e) => `.${e.replace(/^\./, '').toLowerCase()}`);
            OneDrive.open({
                // The picker signs in by itself (implicit flow) to find a work
                // account's drive, so this app needs "Access tokens" under
                // implicit grant enabled - without it: AADSTS700051.
                clientId: pickerClientId,
                action: 'query',
                multiSelect: options?.multiple !== false,
                viewType: 'files',
                advanced: {
                    // No endpointHint: with one, a business drive is browsed
                    // through SharePoint's own API, which refuses this Graph
                    // token (401 "Invalid audience Uri"). Without it the
                    // picker stays on Graph, as it did before F-DMS.
                    accessToken: activeToken,
                    redirectUri: `${window.location.origin}${PICKER_REDIRECT_PATH}`,
                    queryParameters: 'select=id,name,size,file,folder,parentReference',
                    ...(extensions.length ? { filter: ['folder', ...extensions].join(',') } : {}),
                },
                success: (result: any) => {
                    const values: any[] = result?.value || [];
                    resolve(
                        values
                            .filter((item) => !item.folder)
                            .map((item) => ({
                                id: item.id,
                                drive_id: item.parentReference?.driveId,
                                name: item.name,
                            }))
                    );
                },
                cancel: () => resolve(null),
                error: (pickerError: any) => reject(pickerError),
            });
        });
    };

    /** @deprecated Use `pickItems`. Calls back with the picked item ids (not files). */
    const handleOpenMicrosoftPicker = async (onPicked: (items: ExternalPickedItem[]) => void) => {
        try {
            const items = await pickItems();
            if (items && items.length) onPicked(items);
        } catch (err) {
            if (err instanceof IntegrationNotConnectedError) {
                window.location.href = INTEGRATIONS_SETTINGS_PATH;
            }
        }
    };

    const unlockFileOnOneDrive = async (fileId: string) => {
        const url = `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/unlock`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
        });

        if (!response.ok) {
            throw new Error(`Failed to unlock file: ${response.statusText}`);
        }

    };

    const shareAccess = async (
        fileID: string,
        emails: { email: string; roles: ("write" | "read" | "delete" | "rename")[] }[]
    ) => {
        try {
            const client = await getGraphClient();

            const roleMappings: Record<string, string[]> = {
                write: ["write"], 
                read: ["read"], 
                delete: ["write"], 
                rename: ["write"],
            };

            let sharedUsersWithLinks: { email: string; link: string }[] = [];

            for (const { email, roles } of emails) {
                let graphRoles: string[] = [];

                roles.forEach((role) => {
                    if (roleMappings[role]) {
                        graphRoles = [...new Set([...graphRoles, ...roleMappings[role]])];
                    }
                });

                await client.api(`/me/drive/items/${fileID}/invite`).post({
                    requireSignIn: true,
                    sendInvitation: true,
                    roles: graphRoles,
                    recipients: [{ email }],
                });

                const linkType = roles.includes("write") ? "edit" : "view"; 
                const linkResponse = await client.api(`/me/drive/items/${fileID}/createLink`).post({
                    type: linkType, 
                    scope: "anonymous",
                });

                if (linkResponse?.link?.webUrl) {
                    sharedUsersWithLinks.push({ email, link: linkResponse.link.webUrl });
                }
            }

            return {
                message: translate("File sharing completed successfully!"),
                sharedLinks: sharedUsersWithLinks,
            };
        } catch (error) {
            throw error;
        }
    };



    return (
        <MicrosoftGraphContext.Provider
            value={{
                accessToken,
                isSignedIn,
                MSALsignIn,
                signOut,
                uploadFileToOneDrive,
                getEditLink,
                downloadFileFromOneDrive,
                deleteFileFromOneDrive,
                restrictSharing,
                fetchCurrentUserDetails,
                pickItems,
                handleOpenMicrosoftPicker,
                unlockFileOnOneDrive
            }}
        >
            {children}
        </MicrosoftGraphContext.Provider>
    );
};

export const useMicrosoftGraph = () => useContext(MicrosoftGraphContext);
